import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import MuiPopover from '@material-ui/core/Popover';
import MuiPaper from '@material-ui/core/Paper';
import MuiIconButton from '@material-ui/core/IconButton';
import MuiButton from '@material-ui/core/Button';
import MuiBox from '@material-ui/core/Box';
import MuiDivider from '@material-ui/core/Divider';
import MuiTooltip from '@material-ui/core/Tooltip';
import MuiTextField from '@material-ui/core/TextField';
import MuiSelect from '@material-ui/core/Select';
import MuiMenuItem from '@material-ui/core/MenuItem';
import MuiFormControl from '@material-ui/core/FormControl';
import MuiTypography from '@material-ui/core/Typography';
import FilterListIcon from '@material-ui/icons/FilterList';
import ClearIcon from '@material-ui/icons/Clear';

const styles = theme => ({
  filterButton: {
    padding: 2,
    marginLeft: 4,
    verticalAlign: 'top',
  },
  filterActive: {
    color: theme.palette.primary.main,
  },
  popover: {
    minWidth: 250,
    maxWidth: 350,
  },
  filterContent: {
    padding: theme.spacing(2),
  },
  filterActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: theme.spacing(1, 2),
  },
  filterActionButton: {
    marginLeft: theme.spacing(1),
  },
  formControl: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    minWidth: '100%',
  },
  filterTitle: {
    fontWeight: 500,
    marginBottom: theme.spacing(1),
  },
});

/**
 * Enhanced column filter component for Report Currencies with support for month filtering
 */
const ReportCurrencyColumnFilter = props => {
  const {
    classes,
    columnId,
    columnLabel,
    filterType,
    hasFilter,
    onOpenFilter,
    onApplyFilter,
    onClearFilter,
    filterValues,
    onFilterValueChange,
    options,
  } = props;

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleOpenFilter = event => {
    setAnchorEl(event.currentTarget);
    if (onOpenFilter) {
      onOpenFilter(columnId, event.currentTarget);
    }
  };

  const handleCloseFilter = () => {
    setAnchorEl(null);
  };

  const handleApplyFilter = () => {
    if (onApplyFilter) {
      onApplyFilter();
    }
    handleCloseFilter();
  };

  const handleClearFilter = () => {
    if (onClearFilter) {
      onClearFilter();
    }
    handleCloseFilter();
  };

  const handleFilterValueChange = (key, value) => {
    if (onFilterValueChange) {
      onFilterValueChange(key, value);
    }
  };

  // Renders filter content based on type
  const renderFilterContent = () => {
    switch (filterType) {
      case 'text':
        return (
          <>
            <MuiTypography variant="subtitle2" className={classes.filterTitle}>
              Filter by ID
            </MuiTypography>
            <MuiFormControl className={classes.formControl}>
              <MuiTextField
                value={filterValues?.value || ''}
                onChange={e => handleFilterValueChange('value', e.target.value)}
                placeholder="Enter ID..."
                fullWidth
                variant="outlined"
                size="small"
                label="ID"
              />
            </MuiFormControl>
          </>
        );

      case 'month':
        return (
          <>
            <MuiTypography variant="subtitle2" className={classes.filterTitle}>
              Filter by Statement Date (Month)
            </MuiTypography>
            <MuiFormControl className={classes.formControl}>
              <MuiSelect
                value={filterValues?.value || ''}
                onChange={e => handleFilterValueChange('value', e.target.value)}
                fullWidth
                variant="outlined"
                size="small"
                displayEmpty
                label="Select Month">
                <MuiMenuItem value="">
                  <em>All Months</em>
                </MuiMenuItem>
                {options?.map(option => (
                  <MuiMenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MuiMenuItem>
                ))}
              </MuiSelect>
            </MuiFormControl>
          </>
        );

      default:
        return <div>Filter type not supported</div>;
    }
  };

  return (
    <>
      <MuiTooltip title={`Filter ${columnLabel}`}>
        <MuiIconButton
          className={`${classes.filterButton} ${
            hasFilter ? classes.filterActive : ''
          }`}
          onClick={handleOpenFilter}
          size="small"
          aria-label={`Filter ${columnLabel}`}>
          <FilterListIcon fontSize="small" />
        </MuiIconButton>
      </MuiTooltip>

      <MuiPopover
        open={open}
        anchorEl={anchorEl}
        onClose={handleCloseFilter}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        PaperProps={{
          className: classes.popover,
        }}>
        <MuiPaper>
          <MuiBox className={classes.filterContent}>
            {renderFilterContent()}
          </MuiBox>
          <MuiDivider />
          <MuiBox className={classes.filterActions}>
            <MuiButton
              size="small"
              onClick={handleClearFilter}
              startIcon={<ClearIcon />}>
              Clear
            </MuiButton>
            <MuiButton
              size="small"
              color="primary"
              variant="contained"
              onClick={handleApplyFilter}
              className={classes.filterActionButton}>
              Apply
            </MuiButton>
          </MuiBox>
        </MuiPaper>
      </MuiPopover>
    </>
  );
};

ReportCurrencyColumnFilter.propTypes = {
  classes: PropTypes.object.isRequired,
  columnId: PropTypes.string.isRequired,
  columnLabel: PropTypes.string.isRequired,
  filterType: PropTypes.oneOf(['text', 'month']).isRequired,
  hasFilter: PropTypes.bool,
  onOpenFilter: PropTypes.func,
  onApplyFilter: PropTypes.func,
  onClearFilter: PropTypes.func,
  filterValues: PropTypes.object,
  onFilterValueChange: PropTypes.func,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.any.isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
};

ReportCurrencyColumnFilter.defaultProps = {
  hasFilter: false,
  filterValues: {},
  options: [],
};

export default withStyles(styles)(ReportCurrencyColumnFilter);
